import { useEffect, useMemo, useState } from "react";
import { X as XIcon, Check, MessageSquare, Loader2, AlertTriangle, ZoomIn, ClockIcon, FileWarning } from "lucide-react";
import { api } from "../lib/nailApi";

/**
 * Phase 8.4 — Document review drawer.
 *
 * Opens from the GIẤY TỜ column glyph in NailEventAdmin. Consolidates what
 * used to live at /admin/license-verifications. Names first, photo second,
 * three buttons — Duyệt / Từ chối / Xin giấy khác.
 *
 * The parent already has license_verification embedded on the row payload;
 * we accept it as a prop instead of round-tripping to fetch it.
 *
 * Jul 2026 additions (P3):
 *   - Notes History (stacked, newest-first, read-only)
 *   - Access log behind a "lịch sử xem" tap
 *   - File-missing state renders a clear indicator, not a broken image
 *
 * Props:
 *   registration   full registration row with .license_verification embedded
 *   onClose        () => void
 *   onChanged      () => void   fires after a state change so the list refreshes
 *   lang           "en" | "vi"
 */
export default function LicenseReviewDrawer({ registration, onClose, onChanged, lang = "vi" }) {
  const L = (vi, en) => (lang === "en" ? en : vi);
  const verification = registration?.license_verification || null;
  const fileMissing = !!verification?.license_file_missing;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(null); // "approve" | "reject" | "request"
  const [note, setNote] = useState("");
  const [notifyOnReject, setNotifyOnReject] = useState(false); // Phase 8.10.A opt-in
  const [imgZoomed, setImgZoomed] = useState(false);
  const [fileBlobUrl, setFileBlobUrl] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileMissingLocal, setFileMissingLocal] = useState(false);  // 410 fallback
  const effectiveFileMissing = fileMissing || fileMissingLocal;
  const [notes, setNotes] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [logExpanded, setLogExpanded] = useState(false);

  // Fetch the license file. Backend now 302-redirects to a presigned Spaces URL
  // (private, 15-min TTL), so we can either follow it as a blob or use it as
  // an <img src>. We follow-as-blob to keep the caller authenticated.
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    if (!verification?.id || fileMissing) return;
    setFileLoading(true);
    (async () => {
      try {
        const r = await api.get(
          `/organizer/license-verifications/${verification.id}/file`,
          { responseType: "blob" },
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setFileBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) {
          // Defense-in-depth for the file-missing case: backend returns 410
          // with a specific detail string. Treat that as "file missing" rather
          // than a generic error banner.
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          if (status === 410 && detail === "license_file_missing_awaiting_reupload") {
            setFileMissingLocal(true);
          } else {
            setError(e?.response?.data?.detail || L("Không tải được hình", "Could not load photo"));
          }
        }
      } finally {
        if (!cancelled) setFileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [verification?.id, fileMissing]);

  // Fetch notes + access log once when the drawer opens.
  useEffect(() => {
    if (!verification?.id) return;
    (async () => {
      try {
        const [n, a] = await Promise.all([
          api.get(`/organizer/license-verifications/${verification.id}/notes`),
          api.get(`/organizer/license-verifications/${verification.id}/access-log`),
        ]);
        setNotes(n.data?.notes || []);
        setAccessLog(a.data?.entries || []);
      } catch (e) {
        // silent; drawer core function isn't blocked by notes/log fetch failure
      }
    })();
  }, [verification?.id, busy]);

  const registrantName = registration?.owner_name || registration?.business_name || "—";
  const extractedOwner = verification?.ai_extracted_owner_name || "—";
  const businessName = registration?.business_name || verification?.ai_extracted_business_name || "";

  // Quiet hint — never gates the admin decision
  const nameHint = useMemo(() => {
    if (!verification?.id || extractedOwner === "—" || registrantName === "—") return null;
    const norm = (s) => (s || "").toLowerCase()
      .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s]/g, " ").split(/\s+/).filter((t) => t.length > 1);
    const a = new Set(norm(registrantName));
    const b = new Set(norm(extractedOwner));
    const shared = [...a].filter((x) => b.has(x));
    if (shared.length >= 1) return { ok: true, label: L("tên trùng khớp", "names match") };
    return { ok: false, label: L("tên khác nhau", "names differ") };
  }, [registrantName, extractedOwner, verification]);

  async function act(kind) {
    if (!verification?.id) return;
    setBusy(true);
    setError("");
    try {
      const url = {
        approve: `/organizer/license-verifications/${verification.id}/approve`,
        reject: `/organizer/license-verifications/${verification.id}/reject`,
        request: `/organizer/license-verifications/${verification.id}/request-different-document`,
      }[kind];
      const body = { note: note || undefined };
      if (kind === "reject") body.notify_attendee = notifyOnReject;
      await api.post(url, body);
      onChanged?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || L("Có lỗi xảy ra", "Something went wrong"));
    } finally {
      setBusy(false);
      setConfirming(null);
      setNote("");
      setNotifyOnReject(false);
    }
  }

  const meta = verification?.ai_extracted_raw?.meta;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-charcoal/50 backdrop-blur-sm"
      data-testid="license-review-drawer"
    >
      <div className="bg-linen w-full sm:max-w-lg sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[100dvh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-espresso/10 bg-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">
              {L("Xem giấy tờ", "Review document")}
            </div>
            <div className="font-serif text-lg text-espresso truncate">
              {businessName || registrantName}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drawer-close"
            className="p-2 -mr-2 text-espresso-soft hover:text-espresso"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="px-4 py-2.5 text-sm text-[#7A2E2E] bg-[#F5DADA] border-b border-[#7A2E2E]/20" data-testid="drawer-error">
            <AlertTriangle className="inline w-4 h-4 mr-1 -mt-0.5" /> {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {/* Names first — the whole decision */}
          <div className="px-5 py-4 space-y-3 border-b border-espresso/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">
                {L("Đăng ký tên", "Registered as")}
              </div>
              <div className="mt-0.5 font-serif text-xl text-espresso" data-testid="drawer-registered-name">
                {registrantName}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">
                {L("Tên trên giấy tờ", "Owner on document")}
              </div>
              <div className="mt-0.5 font-serif text-xl text-espresso" data-testid="drawer-doc-owner-name">
                {extractedOwner}
              </div>
            </div>
            {nameHint && (
              <div
                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full ${
                  nameHint.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
                }`}
                data-testid="drawer-name-hint"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${nameHint.ok ? "bg-emerald-500" : "bg-amber-500"}`} />
                {nameHint.label}
              </div>
            )}
          </div>

          {/* Photo */}
          <div className="px-5 py-4 bg-white">
            {effectiveFileMissing && (
              <div
                data-testid="drawer-file-missing"
                className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900"
              >
                <FileWarning className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="text-sm leading-snug">
                  <div className="font-semibold">
                    {L("Hồ sơ bị mất khỏi kho lưu trữ.", "Document lost from storage.")}
                  </div>
                  <div className="text-amber-800/90 mt-0.5">
                    {L(
                      "Bạn cần liên hệ hội viên và xin gửi lại giấy tờ. Không tự động gửi tin nhắn.",
                      "Contact the member and ask for a fresh upload. No automated message will fire.",
                    )}
                  </div>
                </div>
              </div>
            )}
            {!effectiveFileMissing && fileLoading && (
              <div className="flex items-center justify-center h-40 text-espresso-soft">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
            {!effectiveFileMissing && !fileLoading && fileBlobUrl && (
              <button
                type="button"
                onClick={() => setImgZoomed(true)}
                data-testid="drawer-photo-tap"
                className="w-full block rounded-xl overflow-hidden border border-espresso/10 bg-linen-100 relative group"
              >
                <img
                  src={fileBlobUrl}
                  alt={L("Giấy tờ đã tải lên", "Uploaded document")}
                  className="w-full max-h-[420px] object-contain"
                />
                <span className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 shadow group-hover:scale-110 transition">
                  <ZoomIn className="w-4 h-4 text-espresso" />
                </span>
              </button>
            )}
            {!effectiveFileMissing && !fileLoading && !fileBlobUrl && !error && (
              <div className="text-center text-sm text-espresso-soft py-6">
                {L("Không có tệp đính kèm.", "No file attached.")}
              </div>
            )}
          </div>

          {/* Meta strip */}
          {meta && (
            <div className="px-5 pb-4 text-[11px] text-espresso-muted font-mono">
              {meta.provider}: {meta.model_id} · {meta.latency_ms}ms
              {meta.error ? ` · ⚠ ${L("trích xuất lỗi — đọc ảnh thủ công", "extraction failed — read the photo manually")}` : ""}
            </div>
          )}

          {/* Notes history — stacked, newest first, read-only */}
          {notes.length > 0 && (
            <div className="px-5 py-3 border-t border-espresso/10" data-testid="drawer-notes-history">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-2">
                {L("Lịch sử ghi chú", "Notes history")}
              </div>
              <ol className="space-y-2">
                {notes.map((n) => (
                  <li key={n.id} className="text-sm bg-linen-100 rounded-lg p-2.5 border border-espresso/10" data-testid={`drawer-note-${n.id}`}>
                    <div className="flex items-center gap-2 text-[11px] text-espresso-soft">
                      <span className="font-semibold">{n.admin_email}</span>
                      <span>·</span>
                      <span className="uppercase tracking-wide">{n.action}</span>
                      <span>·</span>
                      <span>{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                    {n.note && <div className="mt-1 text-espresso whitespace-pre-wrap">{n.note}</div>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Access log — collapsed by default; behind a "lịch sử xem" tap */}
          <div className="px-5 py-2 border-t border-espresso/10">
            <button
              type="button"
              onClick={() => setLogExpanded((x) => !x)}
              data-testid="drawer-access-log-toggle"
              className="text-[11px] text-espresso-soft hover:text-espresso inline-flex items-center gap-1.5"
            >
              <ClockIcon className="w-3 h-3" />
              {L("Lịch sử xem", "Access log")} ({accessLog.length})
            </button>
            {logExpanded && (
              <ol className="mt-2 space-y-1 text-[11px] font-mono text-espresso-muted" data-testid="drawer-access-log">
                {accessLog.map((e) => (
                  <li key={e.id} data-testid={`drawer-access-log-${e.id}`}>
                    {new Date(e.created_at).toLocaleString()} · {e.admin_email} · <b>{e.action}</b>
                    {e.notes ? ` · ${e.notes}` : ""}
                  </li>
                ))}
                {accessLog.length === 0 && (
                  <li className="text-espresso-soft">{L("Chưa có ai xem hồ sơ này.", "Nobody has viewed this document yet.")}</li>
                )}
              </ol>
            )}
          </div>

          {/* Optional note field for approve/reject/request */}
          {confirming && (
            <div className="px-5 pb-4 space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-1.5">
                  {L("Ghi chú (tùy chọn)", "Note (optional)")}
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  data-testid="drawer-note-input"
                  className="w-full bg-white border border-espresso/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
                />
              </div>
              {/* Phase 8.10.A — Opt-in reject SMS. Default OFF; admin must
                  explicitly tick to send the (soft) notification. Otherwise
                  the reject stays silent and goes into the 'cần gọi' list. */}
              {confirming === "reject" && (
                <label
                  className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5 cursor-pointer select-none"
                  data-testid="drawer-notify-reject-row"
                >
                  <input
                    type="checkbox"
                    checked={notifyOnReject}
                    onChange={(e) => setNotifyOnReject(e.target.checked)}
                    data-testid="drawer-notify-reject-toggle"
                    className="mt-0.5 accent-espresso w-4 h-4"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-espresso">
                      {L("Gửi SMS thông báo (nhẹ nhàng)", "Send a soft SMS notification")}
                    </span>
                    <span
                      data-testid="drawer-notify-reject-preview"
                      className="block text-[11px] text-espresso-soft mt-1 leading-snug"
                    >
                      {L(
                        "Mặc định TẮT. Bật để gửi tin nhắn: \"Đăng ký của bạn cần thêm thông tin. Ban tổ chức sẽ liên hệ sớm.\" Nếu để trống, chỉ ghi quyết định — admin gọi lại từ 'cần gọi'.",
                        "OFF by default. If ticked, sends a warm SMS letting the member know a human will follow up. If left off, the decision is recorded silently and the admin calls from the 'cần gọi' list."
                      )}
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        {/* Buttons */}
        {verification?.id ? (
          <div className="grid grid-cols-3 gap-2 p-3 border-t border-espresso/10 bg-white sticky bottom-0">
            {!confirming ? (
              <>
                <button
                  type="button"
                  onClick={() => setConfirming("approve")}
                  disabled={busy}
                  data-testid="drawer-approve-btn"
                  className="inline-flex items-center justify-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2.5 text-sm font-medium hover:bg-espresso-hover disabled:opacity-40"
                >
                  <Check className="w-4 h-4" /> {L("Duyệt", "Approve")}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming("reject")}
                  disabled={busy}
                  data-testid="drawer-reject-btn"
                  className="inline-flex items-center justify-center gap-1.5 bg-white border border-espresso/25 text-espresso rounded-full px-4 py-2.5 text-sm font-medium hover:bg-linen-100 disabled:opacity-40"
                >
                  <XIcon className="w-4 h-4" /> {L("Từ chối", "Reject")}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming("request")}
                  disabled={busy}
                  data-testid="drawer-request-btn"
                  className="inline-flex items-center justify-center gap-1.5 bg-white border border-espresso/25 text-espresso rounded-full px-3 py-2.5 text-sm font-medium hover:bg-linen-100 disabled:opacity-40"
                >
                  <MessageSquare className="w-4 h-4" /> {L("Xin giấy khác", "Ask again")}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => act(confirming)}
                  disabled={busy}
                  data-testid="drawer-confirm-btn"
                  className="col-span-2 inline-flex items-center justify-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2.5 text-sm font-medium hover:bg-espresso-hover disabled:opacity-40"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    confirming === "approve" ? L("Xác nhận duyệt", "Confirm approve") :
                    confirming === "reject" ? (
                      notifyOnReject
                        ? L("Từ chối + Gửi SMS", "Reject + Send SMS")
                        : L("Từ chối (im lặng)", "Reject (silent)")
                    ) :
                    L("Gửi SMS xin giấy khác", "Send SMS asking again")
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirming(null); setNote(""); }}
                  disabled={busy}
                  data-testid="drawer-cancel-btn"
                  className="inline-flex items-center justify-center gap-1.5 bg-white border border-espresso/25 text-espresso rounded-full px-4 py-2.5 text-sm font-medium hover:bg-linen-100 disabled:opacity-40"
                >
                  {L("Hủy", "Cancel")}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="p-5 text-center text-sm text-espresso-soft border-t border-espresso/10 bg-white">
            {L("Người này chưa tải giấy tờ lên.", "This person hasn't uploaded a document yet.")}
          </div>
        )}
      </div>

      {/* Full-screen zoom */}
      {imgZoomed && fileBlobUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setImgZoomed(false)}
          data-testid="drawer-photo-zoomed"
        >
          <img src={fileBlobUrl} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
